<script>
import { required } from "vuelidate/lib/validators";
import validationMessages from '@/components/validations';
import vue2Dropzone from "vue2-dropzone";
import "vue2-dropzone/dist/vue2Dropzone.min.css";
import Games from "@/services/Games";
import Swal from "sweetalert2";

export default {
  name: "modalEditQuizQuestion",
  components: {
    validationMessages,
    vueDropzone: vue2Dropzone,
  },
  data() {
    return {
      csrf_token: localStorage.getItem('csrf_token'),
      showModal: false,
      tryingToEdit: false,
      submitted: false,
      loading: false,
      questionId: null,
      question: { en: '' },
      difficulty: 'medium',
      answers: [
        { answer: { en: "" }, is_correct: false },
        { answer: { en: "" }, is_correct: false },
        { answer: { en: "" }, is_correct: false },
        { answer: { en: "" }, is_correct: false }
      ],
      correctAnswer: null,
      image_url: null,
      existingImage: '',
      originalAnswers: [], // Store original answers with IDs for comparison
      dropzoneOptions: {
        url: '/',
        autoProcessQueue: false,
        addRemoveLinks: true,
        thumbnailWidth: 200,
        thumbnailMethod: 'contain',
        maxFilesize: 4,
        acceptedFiles: 'image/jpeg,image/png,image/gif,image/bmp,image/webp',
        maxFiles: 1,
      },
    };
  },
  validations: {
    question: {
      en: { required },
    },
    difficulty: { required },
    answers: {
      $each: {
        answer: {
          en: { required },
        },
      },
    },
    correctAnswer: {
      required(value) {
        return value !== null && value !== undefined;
      },
    },
  },
  methods: {
    open(question = null) {
      // Open modal immediately
      this.showModal = true;
      
      // If question data is provided, populate with basic data first
      if (question && question.id) {
        this.questionId = question.id;
        // Populate with available data immediately
        this.populateQuestion(question);
        
        // Check if we have detailed data (answers with translations)
        const hasDetailedData = question.answers && 
          Array.isArray(question.answers) && 
          question.answers.length > 0 &&
          question.answers[0].translations;
        
        // If we don't have detailed data, fetch it
        if (!hasDetailedData) {
          this.loading = true;
          this.loadQuestionDetails(question.id);
        }
      } else {
        this.loading = false;
      }
    },
    async loadQuestionDetails(questionId) {
      try {
        const query = `details=1&id=${encodeURIComponent(questionId)}`;
        const response = await Games.getQuizQuestion(query);
        const items = response?.data?.data ?? [];
        const detailedQuestion = Array.isArray(items) ? items[0] : null;
        if (detailedQuestion) {
          this.populateQuestion(detailedQuestion);
        }
      } catch (error) {
        this.failedmsg("Failed to load question details");
        console.error("Error loading question details:", error);
      } finally {
        this.loading = false;
      }
    },
    populateQuestion(question) {
      this.questionId = question?.id || null;
      this.question.en = question?.question || '';
      this.difficulty = question?.difficulty || 'medium';
      const options = question?.options || question?.answers || [];
      
      // Store original answers with IDs for comparison
      this.originalAnswers = [];
      
      if (Array.isArray(options) && options.length) {
        this.answers = options.slice(0, 4).map((option, index) => {
          const answerText = option?.answer?.en || option?.answer || '';
          const isCorrect = option?.is_correct === 1 ||
            option?.is_correct === true ||
            option?.correct_answer === 1 ||
            option?.correct_answer === true;
          
          // Store original answer data with ID
          if (option?.id) {
            this.originalAnswers[index] = {
              id: option.id,
              answer: { en: answerText },
              is_correct: isCorrect
            };
          }
          
          return {
            answer: { en: answerText },
            is_correct: isCorrect,
            id: option?.id || null // Store answer ID if available
          };
        });
        while (this.answers.length < 4) {
          this.answers.push({ answer: { en: "" }, is_correct: false, id: null });
          this.originalAnswers[this.answers.length - 1] = null;
        }
        const correctIdx = this.answers.findIndex(opt => opt.is_correct);
        this.correctAnswer = correctIdx >= 0 ? correctIdx : null;
      } else {
        this.answers = [
          { answer: { en: "" }, is_correct: false, id: null },
          { answer: { en: "" }, is_correct: false, id: null },
          { answer: { en: "" }, is_correct: false, id: null },
          { answer: { en: "" }, is_correct: false, id: null }
        ];
        this.originalAnswers = [null, null, null, null];
        this.correctAnswer = null;
      }
      this.existingImage = question?.image || '';
      this.image_url = null;
      if (this.$refs.editDropzone) {
        this.$refs.editDropzone.removeAllFiles(true);
      }
    },
    async updateQuizQuestion() {
      this.tryingToEdit = true;
      this.submitted = true;
      this.$v.$touch();
      if (this.$v.$invalid) {
        this.tryingToEdit = false;
        return;
      }
      
      // Update is_correct flags based on selected correct answer
      this.answers.forEach((answer, index) => {
        answer.is_correct = index === this.correctAnswer;
      });
      
      try {
        // Step 1: Update question (text, difficulty, image) using editQuizQuestion
        const questionFormData = new FormData();
        questionFormData.append('difficulty', this.difficulty);
        questionFormData.append('question', this.question.en);
        if (this.image_url) {
          questionFormData.append('image', this.image_url);
        }
        questionFormData.append('csrf_token', this.csrf_token);
        questionFormData.append('_method', 'PATCH');
        
        await Games.editQuizQuestion(this.questionId, questionFormData);
        
        // Step 2: Update each answer individually using editQuizAnswer
        const answerUpdatePromises = [];
        
        for (let index = 0; index < this.answers.length; index++) {
          const answer = this.answers[index];
          const originalAnswer = this.originalAnswers[index];
          
          // Skip if answer doesn't have an ID (new answers can't be updated this way)
          if (!answer.id) {
            continue;
          }
          
          // Check if answer text changed
          const textChanged = !originalAnswer || 
            (originalAnswer.answer?.en || '') !== (answer.answer?.en || '');
          
          // Check if correct answer status changed
          const correctStatusChanged = !originalAnswer ||
            originalAnswer.is_correct !== answer.is_correct;
          
          // Update answer text if changed
          if (textChanged) {
            answerUpdatePromises.push(
              Games.editQuizAnswer({
                question_id: this.questionId,
                answer_id: answer.id,
                language: 'en',
                answer: answer.answer.en
              })
            );
          }
          
          // Update correct answer status if changed
          if (correctStatusChanged) {
            answerUpdatePromises.push(
              Games.editQuizAnswer({
                question_id: this.questionId,
                answer_id: answer.id,
                correct_answer: answer.is_correct ? 1 : 0
              })
            );
          }
        }
        
        // Execute all answer updates in parallel
        if (answerUpdatePromises.length > 0) {
          await Promise.all(answerUpdatePromises);
        }
        
        Swal.fire(
          "Quiz Question",
          "Quiz question has been updated successfully",
          "success"
        );
        this.$emit('onRefresh');
        this.closeModal();
      } catch (error) {
        const errorMessage = this.extractError(error);
        this.failedmsg(errorMessage);
      } finally {
        this.tryingToEdit = false;
      }
    },
    selectFileImage(files) {
      setTimeout(() => {
        const queuedFiles = this.$refs.editDropzone?.getQueuedFiles?.() || [];
        if (queuedFiles.length > 1) {
          this.failedmsg('You can only upload one image');
          queuedFiles.slice(1).forEach(file => this.$refs.editDropzone.removeFile(file));
        }
        this.image_url = queuedFiles[0] || null;
        if (this.image_url) {
          this.existingImage = '';
        }
      }, 200);
    },
    extractError(error) {
      if (error?.response?.data?.error) {
        return Array.isArray(error.response.data.error)
          ? error.response.data.error.join(', ')
          : error.response.data.error;
      }
      if (error?.message) {
        return error.message;
      }
      return "An unexpected error occurred";
    },
    closeModal() {
      this.showModal = false;
      this.resetProps();
    },
    resetProps() {
      this.questionId = null;
      this.question = { en: '' };
      this.difficulty = 'medium';
      this.answers = [
        { answer: { en: "" }, is_correct: false },
        { answer: { en: "" }, is_correct: false },
        { answer: { en: "" }, is_correct: false },
        { answer: { en: "" }, is_correct: false }
      ];
      this.correctAnswer = null;
      this.image_url = null;
      this.existingImage = '';
      this.originalAnswers = [];
      this.submitted = false;
      this.tryingToEdit = false;
      this.loading = false;
      if (this.$refs.editDropzone) {
        this.$refs.editDropzone.removeAllFiles(true);
      }
    },
    successmsg(message) {
      this.$bvToast.toast(message, {
        title: 'Success',
        variant: 'success',
        solid: true,
      });
    },
    failedmsg(message) {
      this.$bvToast.toast(message, {
        title: 'Error',
        variant: 'danger',
        solid: true,
      });
    },
  },
};
</script>

<template>
  <b-modal
      id="edit_quiz_question"
      v-model="showModal"
      @hidden="resetProps"
      title="Edit Quiz Question"
      title-class="font-18"
      size="lg"
      centered
  >
    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" class="mb-3"></b-spinner>
      <p class="text-muted">Loading question details...</p>
    </div>
    <form v-else @submit.prevent="updateQuizQuestion">
      <div class="row">
        <div class="col-12 col-lg-12">
          <b-form-group label="Difficulty" class="mb-3">
            <b-form-select
                v-model="difficulty"
                :options="[
                  { value: 'easy', text: 'Easy' },
                  { value: 'medium', text: 'Medium' },
                  { value: 'hard', text: 'Hard' }
                ]"
                :class="{ 'is-invalid': submitted && $v.difficulty.$error }"
            />
            <validationMessages
                v-if="submitted"
                :fieldName="'Difficulty'"
                :validationName="$v.difficulty"
            />
          </b-form-group>
        </div>
        <div class="col-12">
          <b-form-group label="Quiz Question (English)" class="mb-3">
            <b-form-input
                v-model.trim="question.en"
                type="text"
                :class="{ 'is-invalid': submitted && $v.question.en.$error }"
            />
            <validationMessages
                v-if="submitted"
                :fieldName="'Quiz Question'"
                :validationName="$v.question.en"
            />
          </b-form-group>
        </div>
        <div class="col-12">
          <div class="answers-section">
            <label class="form-label mb-3">
              <span>Answers (English)</span>
              <small class="text-muted d-block mt-1">Select one correct answer</small>
            </label>
            <validationMessages
                v-if="submitted && $v.correctAnswer.$error"
                :fieldName="'Correct answer'"
                :validationName="$v.correctAnswer"
            />
            <div
                v-for="(answer, index) in answers"
                :key="index"
                class="answer-item p-1"
            >
              <div class="row align-items-center">
                <div class="col-md-9">
                  <b-form-input
                      v-model.trim="answer.answer.en"
                      :placeholder="`Answer ${index + 1}`"
                      :class="{ 'is-invalid': submitted && $v.answers.$each[index].answer.en.$error }"
                  />
                  <validationMessages
                      v-if="submitted && $v.answers.$each[index].answer.en.$error"
                      :fieldName="`Answer ${index + 1}`"
                      :validationName="$v.answers.$each[index].answer.en"
                  />
                </div>
                <div class="col-md-3">
                  <div class="correct-option d-flex align-items-center justify-content-center">
                    <b-form-radio
                        v-model="correctAnswer"
                        :value="index"
                        :name="'correct-answer-edit'"
                        class="me-2"
                    >
                      Correct
                    </b-form-radio>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-12">
          <div class="form-group">
            <label for="edit_image">Image (optional)</label>
            <div v-if="existingImage && !image_url" class="mb-3">
              <small class="text-muted d-block mb-1">Current image:</small>
              <img :src="existingImage" alt="Quiz" class="img-fluid rounded border" />
            </div>
            <vue-dropzone
                id="edit_image"
                ref="editDropzone"
                :use-custom-slot="true"
                :options="dropzoneOptions"
                @vdropzone-files-added="selectFileImage"
                no-progress-bar
            >
              <div class="dropzone-custom-content">
                <i class="display-4 text-muted bx bxs-cloud-upload"></i>
                <h4>Drop file here or click to upload.</h4>
              </div>
            </vue-dropzone>
          </div>
        </div>
      </div>
      <input type="hidden" name="csrf_token" v-model="csrf_token">
    </form>
    <template v-slot:modal-footer>
      <b-button variant="light" @click="closeModal" :disabled="loading">Close</b-button>
      <b-button variant="primary" @click="updateQuizQuestion" :disabled="tryingToEdit || loading">
        <b-spinner small v-if="tryingToEdit" class="me-2"/>
        Update Question
      </b-button>
    </template>
  </b-modal>
</template>

<style scoped>

.correct-option {
  min-height: 38px;
}
</style>

